import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheckCircle, HiXCircle, HiExclamation } from 'react-icons/hi';
import { productsAPI } from '../services/api';
import { mensajeDeError } from '../utils/apiError';
import { dinero } from '../utils/economia';
import ModalShell from './ModalShell';

/**
 * Todo lo que hay entre este producto y su anuncio, en una sola ventana.
 *
 * Antes los requisitos se descubrían de uno en uno: publicabas, eBay rechazaba
 * por la ubicación; lo arreglabas, rechazaba por las políticas; luego por la
 * categoría; luego por el color. Cuatro muros seguidos, cada uno en mitad de
 * una publicación y con el texto crudo de eBay. Esto los pregunta todos juntos
 * y deja rellenar lo que falte antes de tocar nada.
 */

function Fila({ ok, aviso = false, etiqueta, detalle }) {
    const Icono = ok ? HiCheckCircle : aviso ? HiExclamation : HiXCircle;
    const color = ok ? 'text-green-500' : aviso ? 'text-amber-500' : 'text-red-500';

    return (
        <div className="flex items-start gap-3 py-2">
            <Icono className={`w-5 h-5 flex-shrink-0 mt-0.5 ${color}`} />
            <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">{etiqueta}</p>
                {detalle && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{detalle}</p>}
            </div>
        </div>
    );
}

export default function PublishChecklistModal({ product, onClose, onPublished }) {
    const { t } = useTranslation();

    const [informe, setInforme] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);
    const [categoria, setCategoria] = useState(product.ebay_category_id || '');
    const [ficha, setFicha] = useState({});
    const [publicando, setPublicando] = useState(false);

    const comprobar = useCallback(
        (categoriaId) => {
            setCargando(true);
            setError(null);

            productsAPI
                .publishCheck(product.id, categoriaId ? { categoryId: categoriaId } : {})
                .then((res) => {
                    const datos = res.data.data;
                    setInforme(datos);
                    // Si has elegido categoría, esa manda. Adoptar la que
                    // devuelve el servidor borraría tu elección, y además una
                    // respuesta que llegue tarde pisaría a una más reciente.
                    if (!categoriaId && datos.categoria?.id) setCategoria(datos.categoria.id);
                    // Lo que eBay ya da por bueno o se pudo deducir viene
                    // relleno; lo demás queda en blanco para que lo escribas.
                    setFicha(datos.fichaTecnica?.valores || {});
                })
                .catch((e) => setError(mensajeDeError(e, t('publish_check.failed'))))
                .finally(() => setCargando(false));
        },
        [product.id, t]
    );

    useEffect(() => {
        comprobar(product.ebay_category_id || '');
    }, [comprobar, product.ebay_category_id]);

    const campos = informe?.fichaTecnica?.campos || [];
    const faltan = campos
        .filter((c) => c.requerido)
        .filter((c) => !(ficha[c.nombre]?.[0] || '').trim())
        .map((c) => c.nombre);

    // La ganancia guardada ya descuenta la comisión del destino, así que este
    // aviso por fin salta con los productos que pierden dinero DESPUÉS de ella.
    // `null` es «no se sabe la comisión», no una pérdida: `Number(null)` es 0 y
    // sin esta guarda todo producto sin comisión conocida se anunciaría como
    // ruinoso.
    const pierdeDinero =
        product.profit_margin !== null &&
        product.profit_margin !== undefined &&
        Number(product.profit_margin) <= 0;
    const tienePrecio = Number(product.custom_price) > 0;

    // Un destino puede saber publicar sin tener la comprobación previa al estilo
    // eBay: es el caso de Shopify. Entonces no hay checklist que enseñar —solo
    // una confirmación simple— y lo único que hay que asegurar es el precio.
    const soloPublicable = informe?.soportado === false && informe?.publicable === true;

    const puedePublicar = !publicando && (
        soloPublicable
            ? tienePrecio
            : (
                informe?.soportado !== false &&
                informe?.fuentePermitida &&
                informe?.ubicacion?.ok &&
                informe?.politicas?.ok &&
                Boolean(categoria) &&
                faltan.length === 0
            )
    );

    const cambiarCampo = (nombre, valor) => {
        setFicha((actual) => ({ ...actual, [nombre]: valor ? [valor] : [] }));
    };

    const publicar = async () => {
        setPublicando(true);
        setError(null);

        try {
            // La categoría y la ficha técnica son cosas de eBay. Para un destino
            // que solo publica (Shopify) no hay nada que guardar antes.
            if (!soloPublicable) {
                // Se guarda antes de publicar para no volver a preguntarlo nunca.
                await productsAPI.updateImported(product.id, {
                    aspects: Object.fromEntries(Object.entries(ficha).filter(([, v]) => v?.[0])),
                    ebay_category_id: categoria
                });
            }
            await productsAPI.publishImported(product.id);
            onPublished();
        } catch (e) {
            setError(mensajeDeError(e, t('imported_products.publish_failed')));
            setPublicando(false);
        }
    };

    return (
        <ModalShell
            title={t('publish_check.title')}
            onClose={onClose}
            maxWidth="max-w-2xl"
            footer={
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={publicar}
                        disabled={!puedePublicar}
                        className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {publicando
                            ? t('imported_products.publishing')
                            : pierdeDinero
                                ? t('imported_products.publish_anyway')
                                : t('imported_products.publish')}
                    </button>
                    <button type="button" onClick={onClose} className="btn-secondary flex-1">
                        {t('common.close')}
                    </button>
                </div>
            }
        >
            <p className="text-sm text-slate-500 dark:text-slate-400">{product.custom_title}</p>

            {cargando && (
                <div className="space-y-2" aria-live="polite">
                    <div className="h-4 w-56 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                    <div className="h-28 bg-slate-200 dark:bg-slate-700 rounded animate-pulse"></div>
                </div>
            )}

            {/* Un destino que ni publica (WooCommerce): se dice y no se publica. */}
            {!cargando && informe?.soportado === false && !informe?.publicable && (
                <p className="text-sm text-amber-700 dark:text-amber-300">
                    {t('publish_check.platform_unsupported', { platform: informe.plataforma })}
                </p>
            )}

            {/* Un destino que publica sin checklist (Shopify): confirmación
                simple. Lo único que puede faltar es el precio. */}
            {!cargando && soloPublicable && (
                <div className="space-y-2">
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                        {t('publish_check.shopify_intro')}
                    </p>
                    {!tienePrecio && (
                        <p className="text-sm text-amber-700 dark:text-amber-300">
                            {t('publish_check.needs_price')}
                        </p>
                    )}
                </div>
            )}

            {!cargando && informe?.soportado !== false && (
                <>
                    <div className="divide-y divide-slate-200 dark:divide-slate-700">
                        <Fila
                            ok={informe.fuentePermitida}
                            etiqueta={t('publish_check.source')}
                            detalle={informe.fuentePermitida ? null : t('publish_check.source_blocked')}
                        />
                        <Fila
                            ok={informe.variante?.ok}
                            aviso={!informe.variante?.ok}
                            etiqueta={t('publish_check.variant')}
                            detalle={informe.variante?.nombre || t('publish_check.variant_missing')}
                        />
                        {/* Un anuncio de una sola unidad se termina en su
                            primera venta. No es un error, pero conviene saberlo
                            antes y no después. */}
                        <Fila
                            ok={Number(product.inventory_quantity) > 1}
                            aviso={Number(product.inventory_quantity) <= 1}
                            etiqueta={t('publish_check.units')}
                            detalle={
                                Number(product.inventory_quantity) > 1
                                    ? t('publish_check.units_detail', { n: product.inventory_quantity })
                                    : t('publish_check.units_unknown')
                            }
                        />
                        <Fila
                            ok={informe.ubicacion?.ok}
                            etiqueta={t('publish_check.location')}
                            detalle={informe.ubicacion?.ok ? null : t('publish_check.location_missing')}
                        />
                        <Fila
                            ok={informe.politicas?.ok}
                            etiqueta={t('publish_check.policies')}
                            detalle={informe.politicas?.ok ? null : informe.politicas?.motivo}
                        />
                        <Fila
                            ok={faltan.length === 0}
                            etiqueta={t('publish_check.item_specifics')}
                            detalle={
                                faltan.length === 0
                                    ? t('publish_check.item_specifics_ready', { n: campos.filter((c) => c.requerido).length })
                                    : t('publish_check.item_specifics_missing', { fields: faltan.join(', ') })
                            }
                        />
                        {pierdeDinero && (
                            <Fila
                                ok={false}
                                aviso
                                etiqueta={t('publish_check.loses_money')}
                                detalle={t('publish_check.loses_money_detail', {
                                    profit: dinero(product.profit_margin)
                                })}
                            />
                        )}
                    </div>

                    {/* Categoría. Decide qué exige la ficha técnica y dónde
                        encuentran el anuncio los compradores. */}
                    {informe.alternativas?.length > 0 && (
                        <div>
                            <label htmlFor="categoria" className="label">
                                {t('publish_check.category')}
                            </label>
                            <select
                                id="categoria"
                                value={categoria}
                                onChange={(e) => {
                                    setCategoria(e.target.value);
                                    comprobar(e.target.value);
                                }}
                                className="input"
                            >
                                {informe.alternativas.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.ruta || c.nombre} ({c.id})
                                    </option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-slate-500">{t('publish_check.category_hint')}</p>
                        </div>
                    )}

                    {/* Ficha técnica: los campos que exige esa categoría. */}
                    {campos.filter((c) => c.requerido).length > 0 && (
                        <div className="space-y-3">
                            <h4 className="font-semibold text-slate-900 dark:text-white">
                                {t('publish_check.required_fields')}
                            </h4>

                            {campos
                                .filter((c) => c.requerido)
                                .map((c) => {
                                    const valor = ficha[c.nombre]?.[0] || '';
                                    const id = `aspecto-${c.nombre}`;

                                    return (
                                        <div key={c.nombre}>
                                            <label htmlFor={id} className="label">
                                                {c.nombre}
                                            </label>

                                            {c.soloSeleccion && c.valores.length > 0 ? (
                                                <select
                                                    id={id}
                                                    value={valor}
                                                    onChange={(e) => cambiarCampo(c.nombre, e.target.value)}
                                                    className="input"
                                                >
                                                    <option value="">{t('publish_check.choose')}</option>
                                                    {c.valores.map((v) => (
                                                        <option key={v} value={v}>
                                                            {v}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    id={id}
                                                    type="text"
                                                    value={valor}
                                                    onChange={(e) => cambiarCampo(c.nombre, e.target.value)}
                                                    list={c.valores.length > 0 ? `${id}-sugerencias` : undefined}
                                                    className="input"
                                                />
                                            )}

                                            {!c.soloSeleccion && c.valores.length > 0 && (
                                                <datalist id={`${id}-sugerencias`}>
                                                    {c.valores.map((v) => (
                                                        <option key={v} value={v} />
                                                    ))}
                                                </datalist>
                                            )}
                                        </div>
                                    );
                                })}

                            <p className="text-xs text-slate-500">{t('publish_check.fields_hint')}</p>
                        </div>
                    )}

                    {informe.errores?.length > 0 && (
                        <p className="text-xs text-amber-700 dark:text-amber-300">{informe.errores.join(' · ')}</p>
                    )}
                </>
            )}

            {error && (
                <div className="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}
        </ModalShell>
    );
}
